/**
 * Anfragen, die mit ihrer Ansicht enden.
 *
 * **Wozu.** Ein Bereich, den man verlässt, ließ seine Anfragen bislang weiterlaufen. Ein Browser
 * hält je Host nur etwa sechs Verbindungen offen — die Anfragen des neuen Bereichs stellen sich
 * also hinter die des alten in die Schlange, und der Inhalt füllt sich erst, wenn Ladevorgänge
 * fertig sind, die niemanden mehr interessieren.
 *
 * **Wie.** Ein `AbortController` **je Effekt**, angelegt im Effekt und im Aufräumer abgebrochen.
 * Kein Hook, kein Ref, keine Sonderfälle:
 *
 * ```ts
 * useEffect(() => {
 *     const abbruch = new AbortController();
 *
 *     userAPI.getConsumerOrders(customer, abbruch.signal)
 *         .then(setOrders)
 *         .catch((error) => { if (!wasAborted(error)) melden(error); });
 *
 *     return () => abbruch.abort();
 * }, [customer]);
 * ```
 *
 * **Warum kein Hook, der ein Signal für die ganze Komponente liefert.** React ruft Effekte in der
 * Entwicklung absichtlich zweimal auf — auf, ab, wieder auf. Ein Signal, das den ersten Abbau
 * überlebt, ist danach abgebrochen und jede Anfrage damit tot; eines, das dabei ersetzt wird,
 * ändert sich ohne Neurendern und steht in den Abhängigkeitslisten der Aufrufer weiter als das
 * alte. Ein Controller je Effekt kennt beides nicht: Er entsteht und vergeht mit dem Effekt, der
 * ihn braucht.
 */

/**
 * Ob ein Fehler nur besagt, dass die Antwort niemanden mehr interessiert.
 *
 * Ein solcher Abbruch ist keine Störung und gehört nicht als Fehlermeldung auf den Schirm — die
 * Ansicht, die ihn ausgelöst hat, ist ja fort.
 *
 * **Nur `AbortError`, nicht `TimeoutError`.** Die beiden sehen sich ähnlich und bedeuten
 * Gegenteiliges: Der Abbruch kommt daher, dass *wir* die Anfrage nicht mehr brauchen; die
 * abgelaufene Frist daher, dass *niemand* geantwortet hat. Wer beides verschluckt, lässt einen
 * Aktualisieren-Knopf stumm ins Leere laufen — das Rädchen hört auf, die Daten bleiben alt, und
 * gesagt wird nichts. Verlässt eine Ansicht die Bühne, bricht sie selbst ab und erzeugt
 * `AbortError`; die Frist trifft also nur die, auf deren Antwort noch jemand wartet.
 */
export const wasAborted = (error: unknown): boolean => {
    if (!error || typeof error !== 'object') {
        return false;
    }

    return (error as { name?: unknown }).name === 'AbortError';
};
